import type { FC, MutableRefObject } from "react";
import { forwardRef, useId, useMemo, useRef } from "react";

import { useTranslations } from "@ui/providers/useTranslations";
import classNames from "classnames";

import type {
  DropdownIndicatorProps,
  MenuListProps,
  OptionProps,
  Options,
  Props as SelectDefaultProps,
  SelectInstance,
  ValueContainerProps,
} from "@des/react-select";
import ReactSelect, { components } from "@des/react-select";

import { ChevronDownIcon } from "@jti/brand-icons/chevron-down";
import { Button } from "@jti/core-components/components/Button";
import { Checkbox } from "@jti/core-components/components/Checkbox";
import type { SelectOptionType } from "@jti/fe-common";

import type { InputWrapperProps } from "../components";
import { InputWrapper } from "../components";

import styles from "./DropdownSelect.module.scss";

type StoreLocatorFilterItem = {
  name: string;
  key: string;
  value?: string;
  label?: string;
  isLYO?: boolean;
};

export type DropdownSelectProps = SelectDefaultProps<StoreLocatorFilterItem, boolean> &
  Pick<
    InputWrapperProps,
    | "label"
    | "helperText"
    | "isHelperTextVisible"
    | "isError"
    | "errorText"
    | "isOptional"
    | "containerClassName"
    | "wrapperClassName"
  > & {
    options: StoreLocatorFilterItem[];
    defaultValue?: SelectOptionType;
    dataTestId?: string;
    inputId?: string;
    isIconHidden?: boolean;
    onRestart: () => void;
    onApply: (selectedValues: SelectOptionType[]) => void;
    restartButtonText: string;
    applyButtonText: string;
    selectedItemCount?: number;
  };

type ReactSelectCustomProps = SelectDefaultProps<StoreLocatorFilterItem, boolean> & {
  closeMenu: () => void;
  ref: MutableRefObject<SelectInstance>;
  label?: string;
};

const DropdownIndicatorComponent = (props: DropdownIndicatorProps<StoreLocatorFilterItem, boolean>) => (
  <components.DropdownIndicator {...props}>
    <ChevronDownIcon aria-hidden="true" />
  </components.DropdownIndicator>
);

const OptionComponent = (props: OptionProps<StoreLocatorFilterItem, boolean>) => {
  const { isSelected, label, data, innerProps } = props;
  const optionLabel = label || data.name;

  return (
    <components.Option
      {...props}
      innerProps={{
        ...innerProps,
        role: "checkbox",
        "aria-checked": isSelected,
        "aria-label": optionLabel,
      }}
    >
      <div className={styles.dropdownCheckbox} aria-hidden="true">
        <Checkbox checked={isSelected} onCheckedChange={() => {}} label={optionLabel} tabIndex={-1} />
      </div>
    </components.Option>
  );
};

const SelectMenuList = (props: MenuListProps<StoreLocatorFilterItem, boolean>) => {
  const buttonContainerRef = useRef<HTMLDivElement | null>(null);
  const { __ } = useTranslations();
  const customProps = props.selectProps as typeof props.selectProps & {
    onRestart: () => void;
    onApply: (value: unknown) => void;
    closeMenu: () => void;
    restartButtonText: string;
    applyButtonText: string;
  };
  const { onRestart, onApply, value, closeMenu, restartButtonText, applyButtonText } = customProps;

  const buttonContainerHeight = buttonContainerRef.current?.offsetHeight ?? 0;
  const inlineStyle = useMemo(
    () => ({
      paddingBottom: `${buttonContainerHeight + 5}px`,
    }),
    [buttonContainerHeight]
  );

  return (
    <components.MenuList {...props}>
      <div className="light-theme" style={inlineStyle}>
        {props.children}
      </div>
      <div className={styles.buttonsContainer} ref={buttonContainerRef}>
        <Button
          variant="secondary"
          onClick={() => {
            onRestart();
            closeMenu();
          }}
        >
          {restartButtonText}
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            onApply(value);
            closeMenu();
          }}
        >
          {applyButtonText}
        </Button>
      </div>
    </components.MenuList>
  );
};

const ValueContainer = (props: ValueContainerProps<StoreLocatorFilterItem, boolean>) => {
  const customProps = props.selectProps as typeof props.selectProps & {
    selectedItemCount?: number;
    key?: string;
    label?: string;
  };
  const selectedItemsCount = customProps.selectedItemCount ?? props.getValue().length;
  const selectedInfo = selectedItemsCount ? `(${selectedItemsCount})` : "";

  return (
    <components.ValueContainer {...props} key={customProps.key}>
      {props.children}
      {`${customProps.label} ${selectedInfo}`}
    </components.ValueContainer>
  );
};

const PlaceholderComponent = () => null;

const MultiValueComponent = () => null;

const ClearIndicatorComponent = () => null;

const ReactSelectCustom = forwardRef<SelectInstance<StoreLocatorFilterItem, boolean>, ReactSelectCustomProps>(
  (props, ref) => <ReactSelect<StoreLocatorFilterItem, boolean> {...props} ref={ref} />
);

export const DropdownSelect: FC<DropdownSelectProps> = ({
  id,
  label,
  helperText,
  dataTestId = "dropdownSelectInput",
  isHelperTextVisible = false,
  isError,
  errorText,
  isOptional,
  containerClassName,
  wrapperClassName,
  isIconHidden,
  isMulti = true,
  isSearchable = false,
  closeMenuOnSelect = false,
  hideSelectedOptions = false,
  ...rest
}) => {
  const ref = useRef<SelectInstance<StoreLocatorFilterItem, boolean> | null>(null);
  const generatedId = useId();
  const selectId = id || generatedId;
  const closeMenu = () => {
    if (ref.current) ref.current.blur();
  };

  const customComponents = useMemo(
    () => ({
      DropdownIndicator: DropdownIndicatorComponent,
      Option: OptionComponent,
      MenuList: SelectMenuList,
      ValueContainer: ValueContainer,
      Placeholder: PlaceholderComponent,
      MultiValue: MultiValueComponent,
      ClearIndicator: ClearIndicatorComponent,
    }),
    []
  );

  return (
    <InputWrapper
      htmlFor={selectId}
      errorText={errorText}
      helperText={helperText}
      isError={isError}
      isOptional={isOptional}
      dataTestId={`${dataTestId}-container`}
      containerClassName={classNames(containerClassName, styles.container)}
      wrapperClassName={classNames(wrapperClassName, styles.wrapper)}
      isHelperTextVisible={isHelperTextVisible}
    >
      <ReactSelectCustom
        ref={ref}
        id={id}
        inputId={id}
        aria-label={label}
        label={label}
        isOptionSelected={(option: StoreLocatorFilterItem, selectValue: Options<StoreLocatorFilterItem>) => {
          return !!selectValue.find((v) => v.key === option.key);
        }}
        data-testid={dataTestId}
        className={classNames(styles.inputContainer, {
          [styles.containerError]: isError,
          [styles.hideIcon]: isIconHidden,
        })}
        classNamePrefix="react-select"
        unstyled={true}
        closeMenuOnSelect={closeMenuOnSelect}
        hideSelectedOptions={hideSelectedOptions}
        isMulti={isMulti}
        isSearchable={isSearchable}
        blurInputOnSelect={false}
        backspaceRemovesValue={false}
        components={customComponents}
        closeMenu={closeMenu}
        {...rest}
      />
    </InputWrapper>
  );
};
